import type { ReactNode } from "react";

/**
 * A panel over the lower table (design: ihale/koz sayfası): glass, rounded, your
 * hand still showing under it. Sits at the bottom of the canvas `actions` rect.
 */
export function Sheet({
  title,
  aside,
  children,
  labelId,
  dense = false,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
  labelId: string;
  dense?: boolean;
}) {
  return (
    <div
      role="dialog"
      aria-labelledby={labelId}
      className={`absolute inset-x-0 bottom-0 z-20 mx-auto flex max-w-[560px] animate-rise flex-col rounded-sheet border-[1.5px] border-edge bg-panel-solid shadow-panel backdrop-blur-[16px] ${dense ? "gap-2.5 px-3.5 py-3" : "gap-3.5 px-[18px] pb-[18px] pt-4"}`}
    >
      <div className="flex items-center justify-between gap-3">
        <h2
          id={labelId}
          className={`flex-none font-extrabold ${dense ? "text-[15px]" : "text-[17px]"}`}
        >
          {title}
        </h2>
        {aside ? <span className="truncate text-[13px] text-ink/70">{aside}</span> : null}
      </div>
      {children}
    </div>
  );
}

export type KeyTone = "plain" | "gold" | "pass";

const LOOK: Record<KeyTone, string> = {
  plain:
    "border-[1.5px] border-ink/35 bg-ink/10 text-ink enabled:hover:border-gold enabled:hover:bg-gold enabled:hover:text-gold-ink",
  gold: "btn-gold",
  pass: "border-[1.5px] border-danger-soft bg-ink/8 text-danger-soft",
};

/** A key in a panel (design: 52 px, radius 14); keys you may not press fade and do nothing. */
export function Key({
  label,
  onClick,
  disabled = false,
  tone = "plain",
  dense = false,
  className = "",
}: {
  label: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  tone?: KeyTone;
  dense?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      className={`flex items-center justify-center rounded-key font-extrabold disabled:opacity-35 ${dense ? "h-11 text-[16px]" : "h-[52px] text-[18px]"} ${LOOK[tone]} ${className}`}
      disabled={disabled}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

/**
 * A big choice (design: the trump panel's key): a glyph, a word, a hint. Every tile
 * in a row takes the same share, so the panel keeps its proportions whatever is on offer.
 */
export function OptionTile({
  glyph,
  label,
  hint,
  name,
  dense = false,
  onClick,
}: {
  glyph: ReactNode;
  label?: string | undefined;
  hint?: string | undefined;
  /** The accessible name; the tile's own text is decoration. */
  name: string;
  dense?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={name}
      className={`flex min-w-0 flex-col items-center justify-center gap-1 rounded-[16px] border-[1.5px] border-ink/35 bg-ink/10 enabled:hover:border-gold enabled:hover:brightness-125 ${dense ? "h-16" : "h-[76px]"}`}
      onClick={onClick}
    >
      <span aria-hidden="true" className={`leading-none ${dense ? "text-[24px]" : "text-[30px]"}`}>
        {glyph}
      </span>
      {label ? (
        <span aria-hidden="true" className="text-[13px] font-extrabold leading-tight">
          {label}
        </span>
      ) : null}
      {hint ? (
        <span aria-hidden="true" className="text-[11px] leading-tight text-ink/70">
          {hint}
        </span>
      ) : null}
    </button>
  );
}
